/**
 * Observability dashboard — one-pane-of-glass for the trading agents.
 *
 * Designed for a one-person operation: each section is something you can
 * read in ~2 seconds and decide "fine / needs me." Color codes:
 *
 *   green  — agent up, recent heartbeat, no errors today
 *   yellow — agent up but stale logs OR errors logged today
 *   red    — agent down (systemd inactive/failed)
 *   grey   — outside market hours (no heartbeat expected)
 *
 * Auto-refresh every 30s while the page is open. Polls /api/observability/overview
 * which assembles per-agent status, today's signal/fill counts, and the
 * latest EOD reconciliation report (written by eod_reconcile.py at 16:30 ET).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Badge, Card, Table, Accordion } from "react-bootstrap";

import {
  getOverview, getReconToday, getReconHistory, getReconForDate,
} from "../services/observability.service";
import Loader from "../ui/Spinner.component";
import ErrorPage from "../ui/ErrorPage.component";

const HEALTH_BG = { green: "success", yellow: "warning", red: "danger", grey: "secondary" };
const HEALTH_LABEL = { green: "OK", yellow: "WARN", red: "DOWN", grey: "IDLE" };

function HealthDot({ health }) {
  return <Badge bg={HEALTH_BG[health] || "secondary"}>{HEALTH_LABEL[health] || "?"}</Badge>;
}

function fmtDollar(x) {
  if (x === null || x === undefined) return "—";
  const sign = x >= 0 ? "+" : "−";
  return sign + "$" + Math.abs(x).toFixed(2);
}

function fmtPct(x) {
  if (x === null || x === undefined) return "—";
  return (x >= 0 ? "+" : "") + x.toFixed(2) + "%";
}

function fmtMoney0(x) {
  if (x === null || x === undefined || Number.isNaN(x)) return "—";
  return "$" + Math.round(x).toLocaleString("en-US");
}

// "311m old" is not a readable heartbeat age. Render hours/days past an hour.
function fmtAge(min) {
  if (min === null || min === undefined) return "—";
  if (min < 60) return `${min}m`;
  if (min < 60 * 24) return `${Math.floor(min / 60)}h ${min % 60}m`;
  const d = Math.floor(min / (60 * 24));
  return `${d}d ${Math.floor((min % (60 * 24)) / 60)}h`;
}

function fmtStamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  }) + " ET";
}

function pnlClass(x) {
  if (x === null || x === undefined || x === 0) return "";
  return x > 0 ? "text-success" : "text-danger";
}

function fmtPx(x) {
  return typeof x === "number" && !Number.isNaN(x) ? x.toFixed(2) : "—";
}

// Entry / stop / target triple — renders "—" per leg rather than "undefined".
function fmtPx3(a, b, c) {
  return `${fmtPx(a)} / ${fmtPx(b)} / ${fmtPx(c)}`;
}

// ----- Top banner (overall health) -----
function OverallBanner({ overall, etNow, date }) {
  const total = overall.n_agents ?? (overall.n_red + overall.n_yellow + overall.n_green + overall.n_grey);
  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h4 className="mb-0">Agent Observability</h4>
            <small className="text-muted">
              {total} agents · session {date} ·{" "}
              {overall.in_market_hours
                ? <Badge bg="success">market open</Badge>
                : <Badge bg="secondary">market closed</Badge>}
            </small>
          </div>
          <small className="text-muted">{etNow} ET · auto-refresh 30s</small>
        </div>

        <div className="mt-2">
          <Badge bg="success" className="me-1">{overall.n_green} OK</Badge>
          <Badge bg="warning" className="me-1">{overall.n_yellow} warn</Badge>
          <Badge bg="danger"  className="me-1">{overall.n_red} down</Badge>
          <Badge bg="secondary"className="me-1">{overall.n_grey} idle</Badge>
        </div>

        {/* Two P&L figures on purpose: realised round-trips vs. the equity
            line each agent logged. They disagree whenever a position is still
            open or a fill never made it into slippage.jsonl. */}
        <div className="mt-2 d-flex flex-wrap gap-4">
          <div>
            <small className="text-muted d-block">Signals fired</small>
            <strong>{overall.today_signals_total}</strong>
            <small className="text-muted"> · {overall.today_fills_total ?? 0} filled</small>
          </div>
          <div>
            <small className="text-muted d-block">Realised P&amp;L (closed round-trips)</small>
            <strong className={pnlClass(overall.today_pnl_total)}>
              {fmtDollar(overall.today_pnl_total)}
            </strong>
          </div>
          <div>
            <small className="text-muted d-block">
              Equity change ({overall.n_sessions_closed ?? 0} session
              {overall.n_sessions_closed === 1 ? "" : "s"} closed)
            </small>
            <strong className={pnlClass(overall.today_equity_delta)}>
              {overall.today_equity_delta === null || overall.today_equity_delta === undefined
                ? "— pending"
                : fmtDollar(overall.today_equity_delta)}
            </strong>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

// ----- Per-agent card -----
function AgentCard({ agent }) {
  const sl = agent.today.slippage;
  const lg = agent.today.log;
  const variant = HEALTH_BG[agent.health] || "secondary";
  const universe = lg.universe_size ?? lg.last_cycle?.universe ?? null;
  const ses = agent.last_session_end;
  return (
    <Card className="mb-3" border={variant}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h5 className="mb-0">
              <HealthDot health={agent.health} /> <code>{agent.name}</code>
              <small className="text-muted ms-2">{agent.label}</small>
            </h5>
            {agent.health_reason && (
              <div><small className="text-muted">{agent.health_reason}</small></div>
            )}
            <small>
              systemd: <strong>{agent.systemd.state}</strong>
              {agent.systemd.sub_state && <> ({agent.systemd.sub_state})</>}
              {Number.isFinite(agent.systemd.n_restarts) && <> · restarts: {agent.systemd.n_restarts}</>}
              {agent.log.age_min !== null && <> · heartbeat {fmtAge(agent.log.age_min)} ago</>}
            </small>
          </div>
          <div className="text-end">
            <small className="text-muted">universe</small>
            {/* `Universe loaded: N` only appears in the startup banner, which
                scrolls out of the tail window on a long-running agent. The
                per-cycle line carries the same number, so fall back to it
                instead of rendering an em-dash all day. */}
            <div><strong>{universe ?? "—"}</strong> tickers</div>
          </div>
        </div>

        <Table size="sm" className="mb-2">
          <thead className="table-light">
            <tr>
              <th>Today</th><th className="text-end">Shortlist</th>
              <th className="text-end">Fired</th><th className="text-end">Filled</th>
              <th className="text-end">Closed</th><th className="text-end">Open</th>
              <th className="text-end">P&amp;L $</th><th className="text-end">P&amp;L %</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><small>log/jsonl</small></td>
              <td className="text-end">{lg.today.shortlist}</td>
              <td className="text-end">{lg.today.fire}</td>
              <td className="text-end">{sl.buy_filled}</td>
              <td className="text-end">{sl.sold}</td>
              <td className="text-end">{sl.open_at_check}</td>
              <td className="text-end">{fmtDollar(sl.pnl_dollar)}</td>
              <td className="text-end">{fmtPct(sl.pnl_pct_avg)}</td>
            </tr>
          </tbody>
        </Table>

        {sl.trades && sl.trades.length > 0 && (
          <Accordion className="mb-2">
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <small>Today's trades ({sl.trades.length})</small>
              </Accordion.Header>
              <Accordion.Body className="p-1">
                <Table size="sm" className="mb-0">
                  <thead><tr>
                    <th>Ticker</th><th>Strategy</th><th>Qty</th>
                    <th>Plan E/S/T</th><th>Buy</th><th>Sell</th><th>Reason</th><th>P&amp;L</th>
                  </tr></thead>
                  <tbody>
                    {sl.trades.map((t, i) => (
                      <tr key={`${t.ticker}-${t.order_id || i}`}>
                        <td><strong>{t.ticker}</strong></td>
                        <td>{t.strategy || "—"}</td>
                        <td>{t.qty ?? "—"}</td>
                        <td><small>{fmtPx3(t.planned_entry, t.planned_stop, t.planned_target)}</small></td>
                        <td>{fmtPx(t.buy_fill_px)}</td>
                        <td>{fmtPx(t.sell_fill_px)}</td>
                        <td><small>{t.sell_reason || "open"}</small></td>
                        <td className={pnlClass(t.pnl_dollar)}>
                          {fmtDollar(t.pnl_dollar)}{t.pnl_pct ? ` (${fmtPct(t.pnl_pct)})` : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}

        {lg.today.errors > 0 && (
          <Alert variant="warning" className="py-2 mb-2">
            <strong>{lg.today.errors} error(s) today.</strong>
            {lg.last_error && <pre className="mb-0 mt-1" style={{ fontSize: "0.8em" }}>{lg.last_error}</pre>}
          </Alert>
        )}

        {/* Equity fields come from a regex over the log line, so either side
            can be absent on a truncated tail — don't call .toFixed on null. */}
        {ses && (
          <small className="text-muted">
            <strong>Last session ({ses.session_date}):</strong>{" "}
            {fmtMoney0(ses.start_equity)} → {fmtMoney0(ses.end_equity)}{" "}
            <span className={pnlClass(ses.pnl_dollar)}>
              ({fmtDollar(ses.pnl_dollar)} / {fmtPct(ses.pnl_pct)})
            </span>{" "}
            · {ses.n_trades} trades ({ses.n_wins}W / {ses.n_losses}L)
          </small>
        )}

        <div className="mt-2">
          <small className="text-muted">last log: </small>
          <code className="small text-break">{agent.log.last_line || "—"}</code>
        </div>
      </Card.Body>
    </Card>
  );
}

// ----- System checks (refresh job, Java scanner, infra) -----
// `systemctl list-timers --no-legend` emits fixed columns:
//   NEXT(4 tokens) LEFT(2) LAST(4 tokens) PASSED(2) UNIT ACTIVATES
// We only want the human-readable NEXT and LAST timestamps.
function parseTimerLine(line) {
  if (!line) return null;
  const m = line.match(
    /^(\w{3} \d{4}-\d{2}-\d{2} [\d:]+ \w+)\s+(.+?)\s+(\w{3} \d{4}-\d{2}-\d{2} [\d:]+ \w+)\s+(.+?)\s+\S+\.timer/
  );
  if (!m) return null;
  return { next: `${m[1]} (${m[2].trim()})`, last: `${m[3]} (${m[4].trim()})` };
}

function SystemSection({ system }) {
  const r = system.refresh_tickers || {};
  const j = system.java_scanner || {};
  const timer = parseTimerLine(r.timer_line);
  return (
    <Card className="mb-3">
      <Card.Body>
        <h5 className="mb-3">System checks</h5>
        <Table size="sm">
          <tbody>
            <tr>
              <td><strong>Universe refresh (weekly)</strong></td>
              <td>
                <Badge bg={r.active ? "success" : "secondary"}>{r.active ? "active" : "inactive"}</Badge>{" "}
                {r.last_result && (
                  <small className="ms-1">
                    last result:{" "}
                    <code className={r.last_result === "success" ? "text-success" : "text-danger"}>
                      {r.last_result}
                    </code>
                  </small>
                )}
                {/* `systemctl list-timers` output was previously dumped raw.
                    Split it into the two fields that actually matter. */}
                {timer && (
                  <div><small className="text-muted">
                    next {timer.next} · last ran {timer.last}
                  </small></div>
                )}
              </td>
            </tr>
            <tr>
              <td><strong>Java scanner (daily alerts)</strong></td>
              <td>
                {j.error ? <Badge bg="danger">check failed</Badge> :
                 j.alerts_today > 0 ? <Badge bg="success">{j.alerts_today} alerts written today</Badge> :
                 j.last_alert_age_days !== null && j.last_alert_age_days <= 4
                   ? <Badge bg="secondary">none today</Badge>
                   : <Badge bg="warning">none today</Badge>}
                {j.last_alert_date && (
                  <small className="ms-2 text-muted">
                    last write: {j.last_alert_date}
                    {j.last_alert_age_days !== null && <> ({j.last_alert_age_days}d ago)</>}
                  </small>
                )}
                {j.error && <div><small className="text-danger">{j.error}</small></div>}
              </td>
            </tr>
            <tr>
              <td><strong>Scanner root</strong></td>
              <td><code>{system.scanner_root}</code></td>
            </tr>
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

// ----- Reconciliation section -----
function ReconciliationSection() {
  const { data: today } = useQuery({
    queryKey: ["reconToday"], queryFn: getReconToday, refetchInterval: 60_000,
  });
  const { data: history } = useQuery({
    queryKey: ["reconHistory"], queryFn: getReconHistory, refetchInterval: 5 * 60_000,
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const { data: forDate } = useQuery({
    queryKey: ["reconForDate", selectedDate], queryFn: () => getReconForDate(selectedDate),
    enabled: !!selectedDate,
  });

  const reports = (selectedDate && forDate?.reports) || today?.reports || [];
  const date = (selectedDate && forDate?.date) || today?.date;

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">End-of-day reconciliation</h5>
          <small className="text-muted">
            {date} {selectedDate && (
              <>(<button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                         onClick={() => setSelectedDate(null)}>back to today</button>)</>
            )}
          </small>
        </div>

        <small className="text-muted d-block mb-2">
          Cross-checks each agent's signals → submits → buy fills → closes (log + slippage.jsonl + Alpaca).
          Discrepancies = the agent did something the broker disagrees with, or vice versa.
        </small>

        {reports.length === 0 && <Alert variant="info">No reports yet for {date}. The reconcile job runs at 16:30 ET.</Alert>}

        {reports.map(r => (
          <Card key={r.agent} className="mb-2">
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <strong><code>{r.agent}</code></strong>
                {r.found ? (
                  r.report?.discrepancies?.length > 0
                    ? <Badge bg="danger">{r.report.discrepancies.length} discrepancy(ies)</Badge>
                    : <Badge bg="success">clean</Badge>
                ) : <Badge bg="secondary">no report</Badge>}
              </div>
              {r.found && r.report?.summary && (
                <>
                  <Table size="sm" className="mb-1 mt-2">
                    <tbody>
                      <tr>
                        <td>Shortlists</td><td>{r.report.summary.shortlists ?? "—"}</td>
                        <td>Fires</td><td>{r.report.summary.fires ?? "—"}</td>
                        <td>Buy fills (log)</td><td>{r.report.summary.filled_buys_log ?? "—"}</td>
                        <td>Closes (log)</td><td>{r.report.summary.closes_log ?? "—"}</td>
                      </tr>
                      <tr>
                        <td>Submits (jsonl)</td><td>{r.report.summary.submits_jsonl ?? "—"}</td>
                        <td>Buy fills (jsonl)</td><td>{r.report.summary.buy_fills_jsonl ?? "—"}</td>
                        <td>Sell fills (jsonl)</td><td>{r.report.summary.sell_fills_jsonl ?? "—"}</td>
                        <td>Errors</td><td>{r.report.summary.errors ?? "—"}</td>
                      </tr>
                      <tr>
                        <td>Alpaca orders</td><td>{r.report.summary.alpaca_orders ?? "—"}</td>
                        <td>Alpaca buy fills</td><td>{r.report.summary.alpaca_buy_fills ?? "—"}</td>
                        <td>Alpaca sell fills</td><td>{r.report.summary.alpaca_sell_fills ?? "—"}</td>
                        <td>P&amp;L</td>
                        <td className={pnlClass(r.report.summary.pnl_dollar)}>
                          {fmtDollar(r.report.summary.pnl_dollar)} ({fmtPct(r.report.summary.pnl_pct)})
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                  {r.report.discrepancies?.length > 0 && (
                    <Accordion>
                      <Accordion.Item eventKey="0">
                        <Accordion.Header><small>Discrepancies</small></Accordion.Header>
                        <Accordion.Body className="p-2">
                          {r.report.discrepancies.map((d, i) => (
                            <Alert key={i} variant="warning" className="py-1 mb-1">
                              <strong>{d.type}</strong> {d.ticker && <>· {d.ticker}</>}
                              {d.note && <div><small>{d.note}</small></div>}
                            </Alert>
                          ))}
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        ))}

        {/* History strip */}
        {history?.history && (
          <div className="mt-3">
            <small className="text-muted d-block mb-1">Recent reports (click to view):</small>
            {Object.entries(history.history).map(([agent, dates]) => (
              <div key={agent} className="mb-1">
                <small><code>{agent}</code>:{" "}
                  {dates.length === 0 ? <em className="text-muted">none yet</em> :
                    dates.slice(0, 14).map(d => (
                      <button key={d} type="button"
                              className="btn btn-link btn-sm p-0 me-2 align-baseline"
                              onClick={() => setSelectedDate(d)}>{d}</button>
                    ))}
                </small>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

// ----- Page -----
function Observability() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["observabilityOverview"],
    queryFn: getOverview,
    refetchInterval: 30_000,
  });

  if (isLoading) return <Loader />;
  if (error) return <ErrorPage errorCode="500" description="Could not reach the observability endpoint." />;

  return (
    <div className="container-fluid mt-3">
      <OverallBanner overall={data.overall} etNow={data.et_now} date={data.date} />
      {data.agents.map(a => <AgentCard key={a.name} agent={a} />)}
      <SystemSection system={data.system} />
      <ReconciliationSection />
      <small className="text-muted d-block mt-3">
        Generated {fmtStamp(data.generated_at)} · scanner root: <code>{data.system.scanner_root}</code>
      </small>
    </div>
  );
}

export default Observability;
